import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import useGeoInfo from "../../useGeoInfo";

/**
 * ContactForm – v2 (DB insert + email via contact.php)
 * ---------------------------------------------------
 * • Saves enquiry into `landing_form_tracking` (see PHP endpoint).
 * • Sends an e‑mail via PHPMailer on the backend.
 * • Re‑uses geo/IP meta.  ACCOUNT + PLATFORM constants are sent, too.
 */

const API_BASE = "https://system.yousrygroup.com/api_test/php";
const ENDPOINT = `${API_BASE}/contact.php`;

const ACCOUNT = "Online";
const PLATFORM = "Google";

export default function ContactForm() {
  const { i18n, t } = useTranslation();
  const geo = useGeoInfo();

  const [form, setForm] = useState({
    name: "",
    email: "",
    services: "",
    mobile: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | ok | err

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      await axios.post(ENDPOINT, {
        ...form,
        mobile: `+965${form.mobile}`,
        account: ACCOUNT,
        platform: PLATFORM,
        ...geo, // ip, lat, lng, country, city, region
      });

      setStatus("ok");
      setForm({ name: "", email: "", services: "", mobile: "" });
    } catch (err) {
      console.error(err);
      setStatus("err");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 gap-6 md:grid-cols-2 mt-6"
    >
      {/* ✅ Name Input */}
      <div className="col-span-1">
        <input
          type="text"
          name="name"
          className="form-control w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={t("contact.name_placeholder")}
          value={form.name}
          onChange={handleChange}
          required
        />
      </div>

      {/* ✅ Email Input */}
      <div className="col-span-1">
        <input
          type="email"
          name="email"
          className="form-control w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={t("contact.email_placeholder")}
          value={form.email}
          onChange={handleChange}
          required
        />
      </div>
      {/* ✅ Phone Number Input with +965 prefix and 8-digit limit */}
      <div className="md:col-span-2 col-span-1 flex items-center px-3 py-2 border-gray-300">
        {/* Prefix */}
        <div
          className={`flex items-center bg-gray-100 px-3 py-1 rounded-full ${
            i18n.language === "ar" ? "ml-4" : "mr-4"
          }`}
        >
          <span className="text-gray-800 font-semibold select-none">+965</span>
        </div>

        {/* Input */}
        <input
          type="tel"
          name="mobile"
          placeholder={t("contact.phone_placeholder")}
          className="bg-transparent rounded-md w-full focus:outline-none text-gray-700 placeholder:text-gray-400 px-2"
          value={form.mobile}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d{0,8}$/.test(value)) {
              handleChange({ target: { name: "mobile", value } });
            }
          }}
          required
        />
      </div>

      {/* ✅ Message Input */}
      <div className="md:col-span-2 col-span-1">
        <textarea
          name="services"
          className="form-control w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={t("contact.message_placeholder")}
          rows="5"
          value={form.services}
          onChange={handleChange}
          required
        ></textarea>
      </div>

      {/* Submit */}
      <div className="md:col-span-2 flex justify-center">
        <button
          type="submit"
          disabled={status === "loading"}
          className="btn btn-primary w-full md:w-auto px-6 py-3 rounded-md text-white bg-primary hover:bg-secondary transition-all"
        >
          {status === "loading"
            ? t("contact.sending")
            : t("contact.send_message")}
        </button>
      </div>

      {status === "ok" && (
        <p className="md:col-span-2 text-green-600 text-center">
          {t("contact.success")}
        </p>
      )}
      {status === "err" && (
        <p className="md:col-span-2 text-red-600 text-center">
          {t("contact.error")}
        </p>
      )}
    </form>
  );
}
